import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitChandelierLivrePlieDentellePerles() {
  const { addItem } = useCart();
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [productImage, setProductImage] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);

  // Charger l'image personnalisée au démarrage
  useEffect(() => {
    const loadImage = async () => {
      try {
        const image = await getProductImage('chandelier-livre-plie-dentelle-perles');
        if (image) {
          setProductImage(image);
        }
        setImageLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'image:', error);
        setImageLoaded(true);
      }
    };
    loadImage();
  }, []);

  const product = {
    id: 'chandelier-livre-plie-dentelle-perles',
    name: 'Chandelier en livre plié – Dentelle & Perles (pièce unique)',
    price: 30,
    image: productImage || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/d529905dc80190c446bfa46703479507.png',
    description: `✨ Chandelier en livre plié – Dentelle & Perles (pièce unique)

Ce chandelier décoratif est une création artisanale réalisée à partir d'un livre entièrement plié à la main, transformé en un élégant support pour bougie. Il est orné de dentelle délicate, de perles blanches et d'un ruban aux motifs dorés, pour un rendu raffiné et lumineux.

La bougie placée au sommet diffuse une lumière douce, idéale pour créer une ambiance chaleureuse, chic et poétique pendant les fêtes de Noël.

Chaque création est unique, façonnée avec soin dans mon atelier à partir de livres recyclés. Aucun chandelier ne peut être reproduit à l'identique.

🕯️ Idéal pour :
- Une décoration de table élégante pour Noël  
- Un cadeau artisanal raffiné et original  
- Apporter une touche chaleureuse et lumineuse à un salon ou une entrée  

✅ Création 100 % faite main  
✅ Pièce unique  
✅ Livre recyclé – démarche éco-responsable  
✅ Décorations dentelle, perles et ruban incluses`,
    category: 'Collection de Noël',
    subcategory: 'Chandelier',
    stock: 1,
    isUnique: true
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleAddToCart = () => {
    try {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image
      });
      showNotification(`${product.name} ajouté au panier !`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <nav className="bg-[#FAF9F8] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#B97A72] hover:underline">Accueil</Link>
            <span className="text-[#6B5E5A]">/</span>
            <Link to="/boutique" className="text-[#B97A72] hover:underline">Boutique</Link>
            <span className="text-[#6B5E5A]">/</span>
            <span className="text-[#6B5E5A]">{product.name}</span>
          </div>
        </div>
      </nav>

      {/* Produit */}
      <main className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-2xl overflow-hidden">
              <div className="aspect-square relative">
                {!imageLoaded ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-loader-4-line text-4xl text-[#B97A72] animate-spin"></i>
                  </div>
                ) : (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute top-4 right-4">
                  <span className="bg-[#C49B86] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Pièce unique ✨
                  </span>
                </div>
              </div>
            </div>

            {/* Détails */}
            <div>
              <span className="text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                {product.subcategory}
              </span>
              <h1 className="text-4xl font-bold text-[#6B5E5A] mt-2 mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                {product.name}
              </h1>

              <div className="flex items-baseline space-x-4 mb-6">
                <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price}€
                </span>
              </div>

              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 text-[#6B5E5A]">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-line text-xl text-[#B97A72]"></i>
                  </div>
                  <span className="text-sm font-medium">Article unique – 1 seul exemplaire disponible</span>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                className="w-full bg-[#B97A72] text-white px-8 py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-lg font-medium mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                  <i className="ri-shopping-cart-line"></i>
                </div>
                Ajouter au panier
              </button>

              <div className="prose prose-sm max-w-none">
                <div className="text-[#6B5E5A] whitespace-pre-line leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.description}
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-truck-line text-xl text-[#B97A72]"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#6B5E5A]">Livraison soignée</p>
                    <p className="text-xs text-[#6B5E5A]">Emballage protecteur pour garantir l'intégrité de votre création</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-hand-heart-line text-xl text-[#B97A72]"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#6B5E5A]">Fait main avec amour</p>
                    <p className="text-xs text-[#6B5E5A]">Chaque pièce est unique et réalisée dans mon atelier en Provence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Notification */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
